/* eslint-disable @typescript-eslint/no-floating-promises */
import { Box, Button, Flex, Input, Text, Textarea } from "@chakra-ui/react"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import { type FormDataInterface } from "../../../components/commons/commonTypes"
import { Colors } from "../../../theme/colors"
import {
  editFmpFieldsSnapshotAtom,
  editFmpIsDirtyAtom,
  editFmpRowOrderSnapshotAtom,
  editFmpSaveLoadingAtom,
  editFmpSaveTriggerAtom
} from "../common.fmp.hooks/editFmpDirty.store"
import useFetchFilteredFMP from "../common.fmp.hooks/fetchFilteredFMP.data.hook"
import { formData } from "../common.fmp.hooks/formData.store"
import { useGetHTMLforPDFDownload } from "../common.fmp.hooks/pdfDownload.hook"
import { pdfGeneratorAtom } from "../common.fmp.hooks/pdfGenerator.store"
import { isReorderModeAtom } from "../fmpProjectTable/fmpSortedOrder.store"

interface EditFireManagementPlanFormProps {
  fmpId?: number
  refToPass: React.RefObject<HTMLDivElement>
}

// Preserved: consumed by components/FileUpload/fileUpload.tsx
export const inputStyles = {
  h: "32px",
  borderRadius: "2",
  borderWidth: "1.5px",
  borderColor: `${Colors.SecondaryPrimary.c2}30`,
  fontSize: "12px",
  py: "4px",
  pl: "12px",
  pr: "8px"
}

const textareaStyles = {
  borderRadius: "2",
  borderWidth: "1.5px",
  borderColor: `${Colors.SecondaryPrimary.c2}30`,
  fontSize: "14px",
  p: "8px",
  minH: "60px",
  resize: "vertical" as const
}

const singleLineInputStyles = {
  borderRadius: "2",
  borderWidth: "1.5px",
  borderColor: `${Colors.SecondaryPrimary.c2}30`,
  fontSize: "14px",
  h: "36px",
  px: "8px"
}

// #D97706 (amber) at 50% opacity, applied as an rgba background so only
// the fill is translucent - text/border legibility is unaffected.
const UNSAVED_FIELD_BG = "rgba(217, 119, 6, 0.5)"

// Matches the label-width grid used by the read-only view-fmp InfoField
// (ViewFireManagementPlanForm.support.tsx), so both pages line up the same
// way - just with an editable Input/Textarea in place of the value Text.
const LABEL_WIDTH = "150px"

interface EditableFieldProps {
  label: string
  name: string
  value: string
  placeholder: string
  isDirty: boolean
  multiline?: boolean
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const EditableField = ({ label, name, value, placeholder, isDirty, multiline, onChange }: EditableFieldProps): JSX.Element => (
  <Box display="grid" gridTemplateColumns={`${LABEL_WIDTH} 1fr`} columnGap="8px" alignItems={multiline ? "start" : "center"}>
    <Text variant="RCaptionBold" color={Colors.Primary.c1} fontSize="14px" whiteSpace="nowrap" textAlign="right" pt={multiline ? "8px" : "0"}>
      {label}
    </Text>
    {multiline ? (
      <Textarea
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        focusBorderColor={Colors.Primary.c1}
        rows={3}
        {...textareaStyles}
        bg={isDirty ? UNSAVED_FIELD_BG : undefined}
      />
    ) : (
      <Input
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        focusBorderColor={Colors.Primary.c1}
        {...singleLineInputStyles}
        bg={isDirty ? UNSAVED_FIELD_BG : undefined}
      />
    )}
  </Box>
)

export const EditFireManagementPlanForm: React.FC<EditFireManagementPlanFormProps> = ({ fmpId, refToPass }): JSX.Element => {
  const navigate = useNavigate()
  const [formDataAtom, setFormDataAtom] = useAtom(formData)
  const { generate: generateHTMLForPDF } = useGetHTMLforPDFDownload(refToPass)
  const setPdfGenerator = useSetAtom(pdfGeneratorAtom)
  const [isReorderMode, setIsReorderMode] = useAtom(isReorderModeAtom)
  const fieldsSnapshot = useAtomValue(editFmpFieldsSnapshotAtom)
  const setFieldsSnapshot = useSetAtom(editFmpFieldsSnapshotAtom)
  const setRowOrderSnapshot = useSetAtom(editFmpRowOrderSnapshotAtom)
  const isDirty = useAtomValue(editFmpIsDirtyAtom)
  const saveTrigger = useAtomValue(editFmpSaveTriggerAtom)
  const saveLoading = useAtomValue(editFmpSaveLoadingAtom)

  useEffect(() => {
    setPdfGenerator(() => generateHTMLForPDF)
    return () => {
      setPdfGenerator(null)
    }
  }, [generateHTMLForPDF, setPdfGenerator])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target
    setFormDataAtom((prevState: any) => ({
      ...prevState,
      [name]: value
    }))
  }

  const handleReorderToggle = (): void => {
    setIsReorderMode(prev => !prev)
  }

  const isCompanyNameDirty = Boolean(fieldsSnapshot) && (formDataAtom.companyName || "") !== fieldsSnapshot?.companyName
  const isProjectNameDirty = Boolean(fieldsSnapshot) && (formDataAtom.projectName || "") !== fieldsSnapshot?.projectName
  const isProjectAddressDirty = Boolean(fieldsSnapshot) && (formDataAtom.projectAddress || "") !== fieldsSnapshot?.projectAddress
  const isCompanyAddressDirty = Boolean(fieldsSnapshot) && (formDataAtom.companyAddress || "") !== fieldsSnapshot?.companyAddress
  const isRevisionDirty = Boolean(fieldsSnapshot) && (formDataAtom.revision || "") !== fieldsSnapshot?.revision

  const navigateToProductFilter = (): void => {
    navigate(Paths.FMP.PRODUCT_FILTER, { state: { fmpId } })
  }

  const { filteredFMP } = useFetchFilteredFMP(fmpId)
  useEffect(() => {
    if (filteredFMP?.length) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { project_name, project_address, subcontractor_address, subcontractor_name, revision } = filteredFMP?.[0] ?? {
        project_name: "",
        project_address: "",
        subcontractor_name: "",
        subcontractor_address: "",
        revision: ""
      }
      setFormDataAtom((prevState: any) => ({
        ...prevState,
        projectName: project_name,
        projectAddress: project_address,
        companyName: subcontractor_name,
        companyAddress: subcontractor_address,
        revision
      }))
      setFieldsSnapshot({
        projectName: project_name ?? "",
        companyName: subcontractor_name ?? "",
        projectAddress: project_address ?? "",
        companyAddress: subcontractor_address ?? "",
        revision: revision ?? ""
      })
    }
  }, [filteredFMP, setFormDataAtom, setFieldsSnapshot])

  useEffect(() => {
    const emptyObject = Object.keys(formData)?.reduce((acc: FormDataInterface, key) => {
      return { ...acc, [key]: "" }
    }, {})
    setFormDataAtom(prev => ({
      ...prev,
      ...emptyObject
    }))
    setFieldsSnapshot(null)
    setRowOrderSnapshot(null)
  }, [formData, setFormDataAtom, setFieldsSnapshot, setRowOrderSnapshot])

  return (
    <Box mt="24px">
      <Box display="grid" gridTemplateColumns="1fr 1fr auto" gridTemplateRows="auto auto" columnGap="24px" rowGap="16px" alignItems="start">
        <Box gridColumn="1" gridRow="1">
          <EditableField
            label="Project Name:"
            name="projectName"
            value={formDataAtom.projectName || ""}
            placeholder="Add Project Name"
            onChange={handleInputChange}
            isDirty={isProjectNameDirty}
          />
        </Box>
        <Box gridColumn="2" gridRow="1">
          <EditableField
            label="Contractor Name:"
            name="companyName"
            value={formDataAtom.companyName || ""}
            placeholder="Add Contractor Name"
            onChange={handleInputChange}
            isDirty={isCompanyNameDirty}
          />
        </Box>
        <Box gridColumn="3" gridRow="1">
          <EditableField
            label="Revision:"
            name="revision"
            value={formDataAtom.revision || ""}
            placeholder="Add Revision"
            onChange={handleInputChange}
            isDirty={isRevisionDirty}
          />
        </Box>
        <Box gridColumn="1" gridRow="2">
          <EditableField
            label="Project Address:"
            name="projectAddress"
            value={formDataAtom.projectAddress || ""}
            placeholder="Add Project Address"
            onChange={handleInputChange}
            isDirty={isProjectAddressDirty}
            multiline
          />
        </Box>
        <Box gridColumn="2" gridRow="2">
          <EditableField
            label="Contractor Address:"
            name="companyAddress"
            value={formDataAtom.companyAddress || ""}
            placeholder="Add Contractor Address"
            onChange={handleInputChange}
            isDirty={isCompanyAddressDirty}
            multiline
          />
        </Box>
      </Box>
      <Flex justifyContent="flex-end" alignItems="center" gap="12px" mt="16px">
        <Button variant="Button1Regular" onClick={navigateToProductFilter} whiteSpace="nowrap">
          Edit Systems
        </Button>
        <Button
          variant="Button1Regular"
          onClick={handleReorderToggle}
          bg={isReorderMode ? "#008000" : undefined}
          _hover={isReorderMode ? { bg: "#006400" } : undefined}
          whiteSpace="nowrap"
        >
          {isReorderMode ? "Exit Reorder" : "Reorder Rows"}
        </Button>
        {isDirty && (
          <Button
            variant="Button1RegularAmber"
            onClick={() => { saveTrigger?.() }}
            isLoading={saveLoading}
            whiteSpace="nowrap"
          >
            Save
          </Button>
        )}
      </Flex>
    </Box>
  )
}
